import { HISTORY_CARD_STYLES, HISTORY_SECTION_STATUS, HISTORY_USER_STATES } from 'components/Content/BeautyInsider/BeautyInsiderHistory/historyConstants';
import { mapHistoryYearAtGlanceItems } from 'components/Content/BeautyInsider/BeautyInsiderHistory/mapHistoryYearAtGlanceItems';
import localeUtils from 'utils/LanguageLocale';
import HistoryTabEN from 'components/Content/BeautyInsider/BeautyInsiderHistory/locales/HistoryTab_en_US';
import HistoryTabFR from 'components/Content/BeautyInsider/BeautyInsiderHistory/locales/HistoryTab_fr_CA';
import Empty from 'constants/empty';

const getText = localeUtils.getLocalisedDataFromResource(HistoryTabEN, HistoryTabFR);

const sectionHasError = sectionData => Boolean(sectionData?.error);
const sectionIsLoading = sectionData => Boolean(sectionData?.loading || sectionData?.data === null);

const getProfile = profile => ({
    isSignedIn: Boolean(profile?.isSignedIn),
    isRecognized: Boolean(profile?.isRecognized),
    isBi: Boolean(profile?.isBi)
});

const hasHistoryAccess = profile => profile.isSignedIn || profile.isRecognized;

const getUserState = ({ profile, yearItems }) => {
    if (!hasHistoryAccess(profile)) {
        return HISTORY_USER_STATES.GUEST;
    }

    if (!profile.isBi) {
        return HISTORY_USER_STATES.NON_BI;
    }

    if (yearItems.length === 0) {
        return HISTORY_USER_STATES.EMPTY_BI;
    }

    return HISTORY_USER_STATES.INSIDER;
};

const getPlaceholderCopy = (userState, signedOutCopy, nonBiCopy) => (userState === HISTORY_USER_STATES.GUEST ? signedOutCopy : nonBiCopy);

const getYear = yearAtGlance => yearAtGlance?.data?.currentYear?.year || yearAtGlance?.data?.year;

const buildYearAtGlanceSection = ({
    userState, yearItems, year, yearAtGlance, actions
}) => {
    const displayYear = year || new Date().getFullYear();
    const title = getText('yearAtGlanceTitle', [displayYear]);

    if (userState === HISTORY_USER_STATES.GUEST || (userState === HISTORY_USER_STATES.NON_BI && yearItems.length === 0)) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.PLACEHOLDER,
            cardStyle: HISTORY_CARD_STYLES.GREY,
            items: [],
            placeholder: {
                body: getPlaceholderCopy(userState, getText('yearAtGlanceSignedOut', [displayYear]), getText('yearAtGlanceNonBi', [displayYear]))
            }
        };
    }

    if (sectionHasError(yearAtGlance) && yearItems.length === 0) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.ERROR,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            items: [],
            error: { message: getText('yearAtGlanceError'), retryable: true },
            retry: actions.retryYearAtGlance
        };
    }

    if (sectionIsLoading(yearAtGlance)) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.LOADING,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            items: []
        };
    }

    if (yearItems.length === 0) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.EMPTY,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            items: [],
            emptyState: {
                illustration: '/img/ufe/no-rewards.svg',
                body: getText('yearAtGlanceEmpty', [displayYear]),
                primaryAction: {
                    label: getText('shopNow'),
                    action: actions.shopNow
                }
            }
        };
    }

    return {
        title,
        status: HISTORY_SECTION_STATUS.POPULATED,
        cardStyle: HISTORY_CARD_STYLES.WHITE,
        items: yearItems
    };
};

const defaultActions = {
    retryYearAtGlance: () => {},
    shopNow: () => {}
};

/**
 * Maps raw BI History tab inputs into the presentation contract consumed by the Year at a Glance section.
 *
 * @param {Object} params
 * @param {Object} params.profile Recognized/sign-in/BI profile flags.
 * @param {'US'|'CA'} [params.region='US'] Region used for annual activity filtering.
 * @param {Object} [params.yearAtGlance={}] Year at a Glance DTO with loading/error flags and current-year summary data.
 * @param {Object} [params.actions={}] Optional UI callbacks for retry and shop-now behavior.
 * @returns {Object} History user state and Year at a Glance section presentation data.
 */
const mapHistoryTabPresentation = ({
    profile,
    region = 'US',
    yearAtGlance = Empty.Object,
    actions = Empty.Object
} = Empty.Object) => {
    const normalizedProfile = getProfile(profile);
    const mergedActions = {
        ...defaultActions,
        ...actions
    };
    const yearItems = mapHistoryYearAtGlanceItems({
        rawItems: yearAtGlance.data,
        region,
        getText
    });
    const userState = getUserState({
        profile: normalizedProfile,
        yearItems
    });

    return {
        userState,
        sections: {
            yearAtGlance: buildYearAtGlanceSection({
                userState,
                yearItems,
                year: getYear(yearAtGlance),
                yearAtGlance,
                actions: mergedActions
            })
        }
    };
};

export default mapHistoryTabPresentation;
